import type { ThemeTokenSet } from '@ayumia/contracts';

/**
 * Thème Core par défaut et nomenclature des jetons (ADR-092 §3).
 *
 * Le renderer ne lit que des jetons **sémantiques**, sous forme de variables CSS `--ay-<chemin>` :
 * - actions : `color.action.primary|primary_hover|on_primary`, `color.action.secondary|secondary_hover|on_secondary` ;
 * - accent : `color.accent.default|soft|on_accent` ; marque : `color.brand.logo` (facultatif) ;
 * - surfaces : `color.surface.canvas|default|muted|sunken` ; texte : `color.text.primary|secondary|muted|inverse` ;
 * - bordures : `color.border.default|strong` ; états : `color.status.success|warning|danger|info` (+ `_soft`) ;
 * - focus et voile : `color.focus.ring`, `color.overlay.scrim` ;
 * - cadre : `color.shell.*` (rail, tiroir, barre du haut, badge, avatar, fond) ;
 * - typographie : `typography.family|family_display|family_mono`, `typography.size.xs…xl` ;
 * - rayons `radius.sm|md|lg`, espacements `spacing.xs…xl`, ombres `shadow.sm|md` ;
 * - opacité du monogramme en filigrane du fond : `shell.monogram_opacity` (nombre) ;
 * - tons de statut nommés des pastilles et badges : `color.tone.ok|warn|danger|info|neutral|accent.bg|fg` ;
 * - grammaire « console » des formulaires (v1 `console_kit.dart`, ADR-146 addendum (26)) : `form.page.*` (marges de page
 *   16 / 12 haut / 6 bas, 12 au téléphone), `form.gap.*` (gouttière de la grille de 12 colonnes, entre blocs, liste ↔ détail),
 *   `form.radius.card|control|chip` (16, 12, 99), `form.control.height` (50), `form.label.*` (14,5 gras 800),
 *   `form.caps.*` (en-têtes 11,5 gras 800 en capitales), `form.value.size` (valeur forte, 22), `form.detail.width` (détail 520).
 *
 * Le thème actif du tenant (un artefact `theme` d'origine `customer`, dérivé d'un thème de paquet
 * comme `design_ayumia.theme.forest`) remplace tout ou partie de ces jetons, publié par
 * commande : aucune recompilation du front. `tools/theme-import` convertit un fichier de jetons
 * externe (W3C Design Tokens ou variables CSS) en ce format, et l'inverse.
 *
 * Valeurs par défaut : thème **forest** du design system AYUMIA (`design/ayumia-themes/tokens.json`,
 * décision de l'utilisateur du 2026-10-07, thème actif de NEMROD) — ivoire #F5F2EA, surface
 * #FFFFFF, rail et barre du haut #1C2620, accent vert forêt #2F5D3A, ocre #A36733 pour les
 * alertes (foncé au contraste AA, décision du 2026-10-08, avec `ink_muted` et `warn_ink`) ; mêmes correspondances que l'outil d'import (`SEMANTIC_MAP`). Le voile, les ombres et
 * les tailles ne viennent pas du design system : ce sont les valeurs Core.
 */
export const DEFAULT_THEME: ThemeTokenSet = {
  label: { fr: 'AYUMIA par défaut (forest)', en: 'AYUMIA default (forest)' },
  brand: { product_name: 'AYUMIA' },
  primitives: {
    'color.forest.bg': '#F5F2EA',
    'color.forest.surface': '#FFFFFF',
    'color.forest.surface_muted': '#FAF7F0',
    'color.forest.border': 'rgba(31, 41, 36, 0.08)',
    'color.forest.border_strong': 'rgba(31, 41, 36, 0.14)',
    'color.forest.ink': '#1C2620',
    'color.forest.ink_soft': '#3F4A44',
    'color.forest.ink_muted': '#616A66',
    'color.forest.accent': '#2F5D3A',
    'color.forest.accent_soft': '#DCE8DA',
    'color.forest.accent_ink': '#1B3A23',
    'color.forest.sidebar': '#1C2620',
    'color.forest.sidebar_ink': '#A9B5AE',
    'color.forest.sidebar_active': '#F5F2EA',
    'color.forest.warn': '#A36733',
    'color.forest.warn_soft': '#F5E8D6',
    'color.forest.warn_ink': '#915C2E',
    'color.forest.danger': '#9B3A2E',
    'color.forest.danger_soft': '#F2DDD8',
    'color.forest.success': '#2F5D3A',
    'color.forest.success_soft': '#DCE8DA',
    'color.forest.chip_bg': '#EFE9DA',
    'color.core.scrim': 'rgba(26, 17, 10, 0.45)',
    'color.core.dark.surface': '#26302B',
    'color.core.dark.surface_muted': '#1C2620',
    'space.1': '4px',
    'space.2': '8px',
    'space.4': '16px',
    'space.6': '24px',
    'space.8': '32px',
    'radius.1': '6px',
    'radius.2': '8px',
    'radius.3': '11px',
    'font.body': 'Inter, system-ui, sans-serif',
    'font.display': 'Fraunces, Georgia, serif',
    'font.mono': '"JetBrains Mono", ui-monospace, monospace',
    'font.size.xs': '11px',
    'font.size.sm': '13px',
    'font.size.md': '14px',
    'font.size.lg': '18px',
    'font.size.xl': '22px',
    'shadow.1': '0 1px 2px rgba(31, 41, 36, 0.06)',
    'shadow.2': '0 8px 24px rgba(31, 41, 36, 0.14)',
    'opacity.forest.monogram': 0.05,
    'color.console.violet': '#6E6FC7',
    'color.console.violet_soft': '#EEEEFA',
    'color.console.violet_ink': '#4A4BA3',
    'console.page.inline': '16px',
    'console.page.top': '12px',
    'console.page.bottom': '6px',
    'console.page.mobile': '12px',
    'console.gap.grid': '12px',
    'console.gap.rows': '14px',
    'console.gap.blocks': '14px',
    'console.radius.card': '16px',
    'console.radius.control': '12px',
    'console.radius.chip': '99px',
    'console.control.height': '50px',
    'console.label.size': '14.5px',
    'console.label.weight': 800,
    'console.caps.size': '11.5px',
    'console.caps.weight': 800,
    'console.caps.tracking': '1px',
    'console.value.size': '22px',
    'console.detail.width': '520px',
  },
  semantic: {
    'color.surface.canvas': { ref: 'color.forest.bg' },
    'color.surface.default': { ref: 'color.forest.surface' },
    'color.surface.muted': { ref: 'color.forest.surface_muted' },
    'color.surface.sunken': { ref: 'color.forest.chip_bg' },
    'color.border.default': { ref: 'color.forest.border' },
    'color.border.strong': { ref: 'color.forest.border_strong' },
    'color.text.primary': { ref: 'color.forest.ink' },
    'color.text.secondary': { ref: 'color.forest.ink_soft' },
    'color.text.muted': { ref: 'color.forest.ink_muted' },
    'color.text.inverse': { ref: 'color.forest.surface' },
    'color.action.primary': { ref: 'color.forest.accent' },
    'color.action.primary_hover': { ref: 'color.forest.accent_ink' },
    'color.action.on_primary': { ref: 'color.forest.surface' },
    'color.action.secondary': { ref: 'color.forest.chip_bg' },
    'color.action.secondary_hover': { ref: 'color.forest.accent_soft' },
    'color.action.on_secondary': { ref: 'color.forest.ink' },
    'color.accent.default': { ref: 'color.forest.accent' },
    'color.accent.soft': { ref: 'color.forest.accent_soft' },
    'color.accent.on_accent': { ref: 'color.forest.surface' },
    'color.status.success': { ref: 'color.forest.success' },
    'color.status.success_soft': { ref: 'color.forest.success_soft' },
    'color.status.warning': { ref: 'color.forest.warn' },
    'color.status.warning_soft': { ref: 'color.forest.warn_soft' },
    'color.status.danger': { ref: 'color.forest.danger' },
    'color.status.danger_soft': { ref: 'color.forest.danger_soft' },
    'color.status.info': { ref: 'color.forest.ink_soft' },
    'color.focus.ring': { ref: 'color.forest.accent' },
    'color.overlay.scrim': { ref: 'color.core.scrim' },
    'color.shell.background': { ref: 'color.forest.bg' },
    'color.shell.rail': { ref: 'color.forest.sidebar' },
    'color.shell.rail_text': { ref: 'color.forest.sidebar_ink' },
    'color.shell.rail_text_hover': { ref: 'color.forest.sidebar_active' },
    'color.shell.rail_active': { ref: 'color.forest.accent' },
    'color.shell.rail_active_text': { ref: 'color.forest.sidebar_active' },
    'color.shell.rail_indicator': { ref: 'color.forest.warn' },
    'color.shell.topbar': { ref: 'color.forest.sidebar' },
    'color.shell.topbar_text': { ref: 'color.forest.sidebar_ink' },
    'color.shell.topbar_muted': { ref: 'color.forest.sidebar_ink' },
    'color.shell.drawer': { ref: 'color.forest.surface' },
    'color.shell.drawer_text': { ref: 'color.forest.ink' },
    'color.shell.drawer_header': { ref: 'color.forest.sidebar' },
    'color.shell.drawer_header_text': { ref: 'color.forest.sidebar_active' },
    'color.shell.drawer_section': { ref: 'color.forest.chip_bg' },
    'color.shell.drawer_active': { ref: 'color.forest.accent_soft' },
    'color.shell.drawer_active_text': { ref: 'color.forest.accent_ink' },
    'color.shell.badge': { ref: 'color.forest.warn' },
    'color.shell.badge_text': { ref: 'color.forest.surface' },
    'color.shell.avatar': { ref: 'color.forest.warn' },
    'color.shell.avatar_text': { ref: 'color.forest.surface' },
    'spacing.xs': { ref: 'space.1' },
    'spacing.sm': { ref: 'space.2' },
    'spacing.md': { ref: 'space.4' },
    'spacing.lg': { ref: 'space.6' },
    'spacing.xl': { ref: 'space.8' },
    'radius.sm': { ref: 'radius.1' },
    'radius.md': { ref: 'radius.2' },
    'radius.lg': { ref: 'radius.3' },
    'typography.family': { ref: 'font.body' },
    'typography.family_display': { ref: 'font.display' },
    'typography.family_mono': { ref: 'font.mono' },
    'typography.size.xs': { ref: 'font.size.xs' },
    'typography.size.sm': { ref: 'font.size.sm' },
    'typography.size.md': { ref: 'font.size.md' },
    'typography.size.lg': { ref: 'font.size.lg' },
    'typography.size.xl': { ref: 'font.size.xl' },
    'shadow.sm': { ref: 'shadow.1' },
    'shadow.md': { ref: 'shadow.2' },
    'shell.monogram_opacity': { ref: 'opacity.forest.monogram' },
    'color.tone.ok.bg': { ref: 'color.forest.success_soft' },
    'color.tone.ok.fg': { ref: 'color.forest.success' },
    'color.tone.warn.bg': { ref: 'color.forest.warn_soft' },
    'color.tone.warn.fg': { ref: 'color.forest.warn_ink' },
    'color.tone.danger.bg': { ref: 'color.forest.danger_soft' },
    'color.tone.danger.fg': { ref: 'color.forest.danger' },
    'color.tone.info.bg': { ref: 'color.forest.accent_soft' },
    'color.tone.info.fg': { ref: 'color.forest.accent_ink' },
    'color.tone.neutral.bg': { ref: 'color.forest.chip_bg' },
    'color.tone.neutral.fg': { ref: 'color.forest.ink_soft' },
    'color.tone.accent.bg': { ref: 'color.forest.accent_soft' },
    'color.tone.accent.fg': { ref: 'color.forest.accent_ink' },
    'color.tone.violet.dot': { ref: 'color.console.violet' },
    'color.tone.violet.bg': { ref: 'color.console.violet_soft' },
    'color.tone.violet.fg': { ref: 'color.console.violet_ink' },
    'form.page.inline': { ref: 'console.page.inline' },
    'form.page.top': { ref: 'console.page.top' },
    'form.page.bottom': { ref: 'console.page.bottom' },
    'form.page.mobile': { ref: 'console.page.mobile' },
    'form.gap.grid': { ref: 'console.gap.grid' },
    'form.gap.rows': { ref: 'console.gap.rows' },
    'form.gap.blocks': { ref: 'console.gap.blocks' },
    'form.radius.card': { ref: 'console.radius.card' },
    'form.radius.control': { ref: 'console.radius.control' },
    'form.radius.chip': { ref: 'console.radius.chip' },
    'form.control.height': { ref: 'console.control.height' },
    'form.label.size': { ref: 'console.label.size' },
    'form.label.weight': { ref: 'console.label.weight' },
    'form.caps.size': { ref: 'console.caps.size' },
    'form.caps.weight': { ref: 'console.caps.weight' },
    'form.caps.tracking': { ref: 'console.caps.tracking' },
    'form.value.size': { ref: 'console.value.size' },
    'form.detail.width': { ref: 'console.detail.width' },
  },
  modes: {
    dark: {
      'color.text.primary': { ref: 'color.forest.surface' },
      'color.text.secondary': { ref: 'color.forest.sidebar_ink' },
      'color.text.muted': { ref: 'color.forest.ink_muted' },
      'color.surface.canvas': { ref: 'color.forest.sidebar' },
      'color.surface.default': { ref: 'color.core.dark.surface' },
      'color.surface.muted': { ref: 'color.core.dark.surface_muted' },
      'color.surface.sunken': { ref: 'color.core.dark.surface_muted' },
      'color.shell.background': { ref: 'color.forest.sidebar' },
      'color.shell.drawer': { ref: 'color.core.dark.surface' },
      'color.shell.drawer_text': { ref: 'color.forest.surface' },
    },
  },
};

/** Chemins sémantiques de la nomenclature (plus `color.brand.logo`, facultatif) : ce que `tools/theme-import` et l'écran Thèmes savent cibler. */
export const SEMANTIC_TOKEN_PATHS: readonly string[] = [...Object.keys(DEFAULT_THEME.semantic), 'color.brand.logo'];
